import React, { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import ExpandMoreIcon from 'src/shared/material_icons/navigation/expand_more.svg';
import { Icon } from 'src/components/widgets/icon';
import { T } from 'src/shared/helpers';
import { getAgentTurnDraft } from './agentClient';
import { AgentTaskPreview, AgentArtifactKind, AgentArtifact } from './AgentTaskPreview';
import { AgentDraft } from './types';

interface AgentDraftResultCardProps {
    conversationId: number;
    turnId: number;
    resultNumber: number;
    artifact?: AgentArtifactKind;
    /** Application metadata may advance after this immutable snapshot was published. */
    entityStatus?: AgentArtifact['status'] | 'unused';
    defaultExpanded: boolean;
    /** The latest result can ask for further edits: its action chips portal into this slot (in the question below the
     * card) and describe the change through onRequestEdit. Omitted for older, purely historical snapshots. */
    actionsSlot?: HTMLElement | null;
    onRequestEdit?: (message: string) => void;
    /** A request is in flight: the action chips are disabled until it finishes. */
    busy?: boolean;
}

const Card = styled.section`
    width: min(92%, 760px);
    overflow: hidden;
    border: 1px solid #dce3e8;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 3px 12px rgb(38 50 56 / 7%);
`;

// The collapse control lives in the preview's own header (no separate header row above it): a chevron that turns
// the preview between its single header and the full body.
const CollapseToggle = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: #78909c;
    cursor: pointer;

    &:hover {
        background: #eef2f4;
        color: #455a64;
    }

    ${Icon} {
        transition: transform 160ms ease;
        transform: rotate(0deg);
    }

    &[aria-expanded='true'] ${Icon} {
        transform: rotate(180deg);
    }
`;

// Shown only until the snapshot draft is loaded (or if it fails), so the card still has a header with its revision.
const FallbackHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 20px;
`;

const RevPill = styled.span`
    display: inline-flex;
    align-items: center;
    height: 22px;
    padding: 0 8px;
    border-radius: 999px;
    background: #eceff1;
    color: #78909c;
    font-size: 11px;
    font-weight: 600;
`;

const FallbackMessage = styled.span`
    flex: 1;
    color: #607d8b;
    font-size: 13px;
`;

const readOnlyAction = async () => null;

export const AgentDraftResultCard: FC<AgentDraftResultCardProps> = ({
    conversationId,
    turnId,
    resultNumber,
    artifact,
    entityStatus,
    defaultExpanded,
    actionsSlot,
    onRequestEdit,
    busy
}) => {
    const [expanded, setExpanded] = useState(defaultExpanded);
    const [draft, setDraft] = useState<AgentDraft | null>(null);
    const [loading, setLoading] = useState(false);
    const [failed, setFailed] = useState(false);

    useEffect(() => setExpanded(defaultExpanded), [defaultExpanded, turnId]);

    // Fetched even while collapsed: the single header shows the entity and status pills, which come from the draft.
    useEffect(() => {
        if (draft || loading || failed) return;
        const controller = new AbortController();
        setLoading(true);
        void getAgentTurnDraft(conversationId, turnId, controller.signal)
            .then((result) => {
                if (!controller.signal.aborted) {
                    setDraft(result);
                    setFailed(!result);
                }
            })
            .catch(() => {
                if (!controller.signal.aborted) setFailed(true);
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });
        return () => {
            controller.abort();
            setLoading(false);
        };
        // `loading` is intentionally excluded: including it would let setLoading(true) re-run this effect, whose
        // cleanup aborts the request in flight, so it would loop on aborted fetches and never load.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversationId, draft, failed, turnId]);

    return (
        <Card>
            {draft ? (
                <AgentTaskPreview
                    draft={draft}
                    artifact={artifact}
                    entityStatus={entityStatus}
                    // A snapshot is never edited in place; the action chips (when provided) describe changes to the agent.
                    readOnly
                    disabled={busy}
                    showResultNumber={false}
                    // One header only: the collapse chevron and the revision pill sit in the preview's own header,
                    // replacing the separate "Result N" row that used to sit above it.
                    collapsed={!expanded}
                    revisionLabel={`${T._('rev.', 'Admin')}${resultNumber}`}
                    headerAction={
                        <CollapseToggle
                            type='button'
                            aria-expanded={expanded}
                            aria-label={expanded ? T._('Collapse', 'Admin') : T._('Show details', 'Admin')}
                            onClick={() => setExpanded((value) => !value)}
                        >
                            <Icon>
                                <ExpandMoreIcon />
                            </Icon>
                        </CollapseToggle>
                    }
                    // The chips render outside the output, in the "what to do now" question below the card (null when
                    // this is an older snapshot with no follow-up question).
                    actionsSlot={actionsSlot ?? null}
                    onRequestEdit={onRequestEdit}
                    onUpsertPrintArea={readOnlyAction}
                    onDeletePrintArea={readOnlyAction}
                    onRestorePrintArea={readOnlyAction}
                    onSetSideScale={readOnlyAction}
                    onReplaceSideImage={readOnlyAction}
                />
            ) : (
                <FallbackHeader>
                    <RevPill>
                        {T._('rev.', 'Admin')}
                        {resultNumber}
                    </RevPill>
                    <FallbackMessage>{failed ? T._('This draft snapshot could not be loaded.', 'Admin') : `${T._('Loading', 'Admin')}…`}</FallbackMessage>
                </FallbackHeader>
            )}
        </Card>
    );
};
